import React from 'react';
import { View, Text, StyleSheet, Image, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Dropdown } from 'react-native-element-dropdown';
import { Colors, Fonts, Radii, ControlHeight, Shadows, Typography } from '../constants';
import { useResponsive } from '../utils/responsive';

interface SelectOption {
  label: string;
  value: string;
  imageUrl?: string;
}

interface SelectProps {
  label?: string;
  value: string;
  options: SelectOption[];
  onValueChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  style?: any;
  labelStyle?: any;
  dropdownPosition?: 'auto' | 'top' | 'bottom';
}

export const Select: React.FC<SelectProps> = ({
  label,
  value,
  options,
  onValueChange,
  placeholder,
  error,
  style,
  labelStyle,
  dropdownPosition = 'auto',
}) => {
  const selectedOption = options.find(opt => opt.value === value);
  const [open, setOpen] = React.useState(false);
  const { isDesktop } = useResponsive();

  const renderItem = (item: SelectOption, selected?: boolean) => (
    <View
      style={[styles.dropdownItem, selected && styles.dropdownItemSelected]}
      testID={`dropdown-option-${item.value}`}
    >
      {item.imageUrl && (
        <Image
          source={{ uri: item.imageUrl }}
          style={styles.optionImage}
        />
      )}
      <Text
        style={[styles.itemText, selected && styles.itemTextSelected]}
        selectable={false}
        testID={`dropdown-option-text-${item.value}`}
      >
        {item.label}
      </Text>
    </View>
  );

  const renderLeftIcon = () => {
    if (selectedOption?.imageUrl) {
      return (
        <Image
          source={{ uri: selectedOption.imageUrl }}
          style={styles.selectedImage}
        />
      );
    }
    return null;
  };

  return (
    <View style={[styles.container, style]} testID={`select-${label || 'dropdown'}`}>
      {label ? <Text style={[styles.label, labelStyle]}>{label}</Text> : null}
      <Dropdown
        data={options}
        labelField="label"
        valueField="value"
        value={value}
        onChange={(item) => onValueChange(item.value)}
        placeholder={placeholder}
        placeholderStyle={styles.placeholderText}
        selectedTextStyle={styles.selectText}
        style={[styles.dropdown, open && styles.dropdownOpen, error && styles.dropdownError]}
        containerStyle={styles.dropdownContainer}
        itemContainerStyle={styles.itemContainer}
        activeColor="transparent"
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        dropdownPosition={dropdownPosition}
        // The library decides between an anchored menu and a centred modal from
        // a phone/tablet heuristic (pixel density × window size + orientation).
        // A desktop browser window narrower than ~912 CSS px on a retina screen
        // counts as a "phone in landscape" and gets the detached modal, so on the
        // desktop layout we force the menu to anchor under the trigger.
        mode={isDesktop ? 'auto' : 'default'}
        maxHeight={300}
        renderItem={renderItem}
        renderLeftIcon={renderLeftIcon}
        renderRightIcon={() => (
          <Ionicons
            name={open ? 'chevron-up-outline' : 'chevron-down-outline'}
            size={16}
            color={Colors.textMedium}
            style={styles.chevron}
          />
        )}
        testID="dropdown-trigger"
      />
      {error ? <Text style={styles.errorText}>{error}</Text> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    ...Typography.label,
    color: Colors.textDark,
    marginBottom: 8,
  },
  dropdown: {
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.control,
    paddingHorizontal: 16,
    paddingVertical: 0,
    backgroundColor: Colors.white,
    height: ControlHeight.md,
  },
  dropdownOpen: {
    borderColor: Colors.primary,
    ...(Platform.OS === 'web' && Shadows.focusRing),
  },
  dropdownError: {
    borderColor: Colors.error,
  },
  dropdownContainer: {
    borderRadius: Radii.card,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    backgroundColor: Colors.white,
    overflow: 'hidden',
    padding: 6,
    ...Shadows.cardHover,
  },
  selectText: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.text,
  },
  placeholderText: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.textLight,
  },
  itemContainer: {
    backgroundColor: Colors.white,
    borderRadius: Radii.thumb,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: Radii.thumb,
    // A click on an option must always register as a press: without this the
    // browser starts a text selection on the label instead of filtering.
    ...(Platform.OS === 'web' && { userSelect: 'none' as const }),
  },
  dropdownItemSelected: {
    backgroundColor: Colors.primaryLight,
  },
  itemText: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.text,
    ...(Platform.OS === 'web' && { userSelect: 'none' as const }),
  },
  itemTextSelected: {
    fontFamily: Fonts.semiBold,
    color: Colors.accentStrong,
  },
  optionImage: {
    width: 24,
    height: 24,
    borderRadius: 12,
    resizeMode: 'contain',
  },
  selectedImage: {
    width: 24,
    height: 24,
    borderRadius: 12,
    marginRight: 8,
    resizeMode: 'contain',
  },
  chevron: {
    marginLeft: 8,
  },
  errorText: {
    ...Typography.meta,
    color: Colors.error,
    marginTop: 4,
  },
});
